<script setup>
  import {inject, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import AiRephrase from "@images/ai-opt-in-dialog/ai-rephrase.webp";
  import AiSummary from "@images/ai-opt-in-dialog/ai-summary.webp";
  import {useForm, usePage} from "@inertiajs/vue3";
  import {MedButton, MedCarousel, MedCarouselItem} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentAiOptInDialog",
  });

  const privacy = inject("$privacy");
  const page = usePage();
  const refDialog = ref(null);

  const open = () => {
    refDialog.value.open();
  };

  // this is a subset of the configuration form from Settings/Show.vue
  const form = useForm({
    activateAiFeatures: page.props.can?.activate_ai_features ?? false,
    // whenever this form is saved, we store a second flag which indicates, if the user has made an active decision regarding ai feature activation
    hideAiOptInDialog: true,
  });

  const close = () => {
    refDialog.value.close();
  };

  const allow = () => {
    form
      .transform((data) => ({
        ...data,
        activateAiFeatures: true,
      }))
      .post(
        route("settings.store", {
          user: privacy.getCurrentUser().id,
        }),
        {
          preserveScroll: true,
          only: ["flash", "can"],
          onSuccess: () => {
            close();
          },
        },
      );
  };

  const deny = () => {
    form
      .transform((data) => ({
        ...data,
        activateAiFeatures: false,
      }))
      .post(
        route("settings.store", {
          user: privacy.getCurrentUser().id,
        }),
        {
          preserveScroll: true,
          only: ["flash", "can"],
          onSuccess: () => {
            close();
          },
        },
      );
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-width="large"
    dialog-type="emptyActions"
    dialog-title="Entdecken Sie neue KI-Funktionen im MediCheck!"
    primary-label="KI-Funktionen aktivieren"
    :test-id="getTestId('ai-opt-in-dialog')"
  >
    <template #content>
      <p>
        Wir arbeiten stetig daran, Medikationsanalysen für alle Beteiligten besser, schneller und einfacher zu machen.
      </p>
      <p>
        Hierfür stehen Ihnen ab sofort auch
        <strong class="font-semibold">KI-unterstützte Funktionen</strong>
        zur Verfügung, sofern Sie der Nutzung zustimmen.
      </p>
      <p>Der Schutz Ihrer Angaben und der darin enthaltenen Informationen ist jederzeit gewährleistet.</p>

      <ComponentAlert type="info" class="mt-4">
        Sie können Ihre Entscheidung jederzeit in Ihren persönlichen Einstellungen ändern.
      </ComponentAlert>

      <MedCarousel variant="primary">
        <MedCarouselItem>
          <img
            :src="AiRephrase"
            alt="Schematische Darstellung eines Analyseergebnisses. Im Bereich „Patient“ befindet sich ein Button „Sprache vereinfachen“"
            class="w-full rounded-lg object-cover"
          />
        </MedCarouselItem>
        <MedCarouselItem>
          <img
            :src="AiSummary"
            alt="Schematische Darstellung einer Analyseübersicht. Im Bereich „Zusammenfassung der Analyse“ befindet sich ein hervorgehobener Button „Zusammenfassung erstellen“, der die KI-gestützte Generierung einer zielgruppengerechten Zusammenfassung auslöst."
            class="w-full rounded-lg object-cover"
          />
        </MedCarouselItem>
      </MedCarousel>
    </template>

    <template #actions>
      <div class="mt-4 flex w-full flex-row-reverse flex-wrap justify-between">
        <div class="flex flex-row-reverse space-x-3 space-x-reverse">
          <MedButton :data-test="getTestId('ai-opt-in-dialog-allow-button')" variant="primary" @click="allow()">
            KI-Funktionen aktivieren
          </MedButton>

          <MedButton :data-test="getTestId('ai-opt-in-dialog-deny-button')" @click="deny()">
            MediCheck ohne KI nutzen
          </MedButton>
        </div>

        <MedButton flat variant="primary" :data-test="getTestId('ai-opt-in-dialog-ask-later-button')" @click="close()">
          Später erneut fragen
        </MedButton>
      </div>
    </template>
  </ComponentDialog>
</template>
